import { memo } from 'react';
import AddNewAccess from './addNewAccess';
import AccessItem from './accessItem';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useTemplateAccess } from './hooks/useTemplateAccess';
import { COMPONENT_CONSTANTS } from './constants';
import { MainCnt } from './style';
import { AccessEntry, TEMPLATE_ACCESS } from './utils';
import { GroupData } from './types';
import { cloneDeep } from 'lodash';

/**
 * Component for managing template access permissions
 * Displays grouped access entries with role-based permissions
 */
const TemplateAndSubmissionAccess = memo(
  ({
    type,
    data,
    onUpdate,
  }: {
    type: string;
    data: any;
    onUpdate: (key: string, data: any) => void;
  }) => {
    const { groupedDataEntries, setGroupedData, groupedData, addNewGroup } =
      useTemplateAccess(data, type);

    const groupIntoLinear = (data: [string, GroupData][]): AccessEntry[] => {
      const accessEntries: any = [];
      data?.forEach(([_, groupData]) => {
        if (groupData?.isEveryone) {
          accessEntries.push({
            AccessorId: null,
            ...(type === TEMPLATE_ACCESS
              ? { role: groupData.role }
              : { permission: groupData.permission }),
            isEveryone: groupData.isEveryone,
            groupId: groupData.groupId,
            ...(type !== TEMPLATE_ACCESS && { canEdit: groupData.canEdit }),
          });
        }
        groupData?.AccessorIds?.forEach((accessorId) => {
          accessEntries.push({
            AccessorId: accessorId,
            ...(type === TEMPLATE_ACCESS
              ? { role: groupData.role }
              : { permission: groupData.permission }),
            isEveryone: groupData.isEveryone,
            groupId: groupData.groupId,
            ...(type !== TEMPLATE_ACCESS && { canEdit: groupData.canEdit }),
          });
        });
      });

      return accessEntries;
    };

    const updateHandler = (groupId: string, newGroupData: GroupData) => {
      const key =
        type === TEMPLATE_ACCESS ? 'ChecklistAccess' : 'SubmissionAccess';

      const updatedGroups = {
        ...groupedData,
        [groupId]: newGroupData,
      };

      setGroupedData(updatedGroups);

      const linearData = groupIntoLinear(Object.entries(updatedGroups ?? {}));
      onUpdate(key, linearData);
    };

    const removeHandler = (groupId: string) => {
      const key =
        type === TEMPLATE_ACCESS ? 'ChecklistAccess' : 'SubmissionAccess';

      const updatedGroups = cloneDeep(groupedData);
      delete updatedGroups?.[groupId];
      setGroupedData(updatedGroups);

      const linearData = groupIntoLinear(Object.entries(updatedGroups ?? {}));
      onUpdate(key, linearData);
    };
    // Transform grouped data into linear form
    return (
      <MainCnt>
        <Stack className="header" flexDirection="row" alignItems="center">
          <Box
            className="labelBox"
            sx={{
              flex: COMPONENT_CONSTANTS.LAYOUT.ASSIGNEE_FLEX,
            }}
          >
            <Typography className="label">
              {COMPONENT_CONSTANTS.HEADERS[type]}
            </Typography>
          </Box>
          <Box
            className="labelBox"
            sx={{ flex: COMPONENT_CONSTANTS.LAYOUT.PERMISSION_FLEX }}
          >
            <Typography className="label">
              {COMPONENT_CONSTANTS.HEADERS.PERMISSION}
            </Typography>
          </Box>
        </Stack>

        <Divider className="separator" />

        {groupedDataEntries.map(([groupId, groupData]) => (
          <div key={groupId}>
            <AccessItem
              access={groupData}
              updateGroupedData={updateHandler}
              removeGroup={removeHandler}
              type={type}
            />
            <Divider className="separator" />
          </div>
        ))}
        <AddNewAccess addNewGroup={addNewGroup} type={type} />
      </MainCnt>
    );
  },
);

TemplateAndSubmissionAccess.displayName = 'TemplateAndSubmissionAccess';

export default TemplateAndSubmissionAccess;
